/**
 * PendingReviewPanel — pending-review candidates with manual approve/reject
 * and honest adjudication state.
 *
 * Campaign `auteur-hosted-brain-learning-v1`, task H18, over H08's
 * `/v1/brain/memory/candidates` routes.
 *
 * HONEST ADJUDICATION STATE (OD-5, DESIGN §5)
 *   Each candidate displays its claim, origin, source reference, and
 *   adjudication state exactly as the server returned it:
 *   - `reviewed_by_your_provider`: displays provider and model labels.
 *   - `awaiting_review`: no connected provider answered, or all evaluators
 *     are unavailable; links plainly to Provider Keys (`/settings/provider-keys`).
 *   - `conflict`: displays the conflict set; the person chooses, nothing is
 *     auto-picked.
 *
 * NO CLIENT-SIDE LIFECYCLE AUTHORITY
 *   Approve and Reject buttons call the server (`POST /v1/brain/memory/candidates/{id}/review`).
 *   The UI never decides lifecycle state itself and never shows a candidate
 *   as trusted memory before the server says it is.
 *
 * HONEST EMPTY AND UNAVAILABLE STATES
 *   A genuine empty state explains that there is nothing pending review.
 *   A 404 or 503 renders the honest Unavailable state, never fabricated rows.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  BrainApiError,
  CandidateAdjudicationView,
  MemoryCandidateView,
  fetchPendingMemoryCandidates,
  reviewMemoryCandidate,
} from '../../api/brain';

export interface PendingReviewPanelProps {
  projectId: string;
}

type LoadState = 'loading' | 'error' | 'unavailable' | 'ready';

export function AdjudicationDisplay({
  adjudication,
}: {
  adjudication: CandidateAdjudicationView | null;
}): React.ReactElement {
  if (!adjudication) {
    return (
      <div className="pb-candidate-adjudication pb-adjudication-awaiting">
        <span className="pb-adjudication-state">awaiting_review</span>
        <p className="pb-adjudication-note">
          No connected provider, or every evaluator unavailable — awaiting review.
          Connect your provider key in <a href="/settings/provider-keys">Provider Keys</a>.
        </p>
      </div>
    );
  }

  const { state, provider, model, reason, conflict_set_ids, conflicts_with } = adjudication;

  if (state === 'reviewed_by_your_provider') {
    return (
      <div className="pb-candidate-adjudication pb-adjudication-reviewed">
        <span className="pb-adjudication-state">reviewed_by_your_provider</span>
        <span className="pb-adjudication-provider">Provider: {provider || 'not_connected'}</span>
        <span className="pb-adjudication-model">Model: {model || 'not_selected'}</span>
        {reason ? <span className="pb-adjudication-reason">Reason: {reason}</span> : null}
      </div>
    );
  }

  if (state === 'conflict') {
    return (
      <div className="pb-candidate-adjudication pb-adjudication-conflict">
        <span className="pb-adjudication-state">conflict</span>
        <p className="pb-conflict-note">
          Contradicts existing project memory. The person chooses — nothing is auto-picked.
        </p>
        {reason ? <span className="pb-adjudication-reason">Reason: {reason}</span> : null}
        <div className="pb-conflict-set">
          <span className="pb-conflict-label">Conflict set:</span>
          {conflict_set_ids && conflict_set_ids.length > 0 ? (
            <ul className="pb-conflict-ids">
              {conflict_set_ids.map((id) => (
                <li key={id}>
                  <code className="pb-conflict-id">{id}</code>
                </li>
              ))}
            </ul>
          ) : null}
          {conflicts_with && conflicts_with.length > 0 ? (
            <div className="pb-conflicts-with">
              <span className="pb-conflicts-with-label">Conflicting records:</span>
              <ul className="pb-conflicting-records">
                {conflicts_with.map((gid) => (
                  <li key={gid}>
                    <code className="pb-conflicting-record-id">{gid}</code>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const reasonStr = (reason || '').toLowerCase();
  const isNoProvider =
    reasonStr.includes('no_connection') ||
    reasonStr.includes('not_connected') ||
    provider === 'not_connected';
  const isEvaluatorsUnavailable =
    reasonStr.includes('system_one_unavailable') ||
    reasonStr.includes('backend_unavailable') ||
    reasonStr.includes('evaluator') ||
    reasonStr.includes('all_down') ||
    (!isNoProvider && reasonStr.includes('unavailable'));

  let noteText: string;
  if (isNoProvider && !isEvaluatorsUnavailable) {
    noteText = 'No connected provider — awaiting review.';
  } else if (isEvaluatorsUnavailable) {
    noteText = 'Evaluators unavailable — awaiting review.';
  } else {
    noteText = 'No connected provider, or every evaluator unavailable — awaiting review.';
  }

  return (
    <div className="pb-candidate-adjudication pb-adjudication-awaiting">
      <span className="pb-adjudication-state">awaiting_review</span>
      {reason ? <span className="pb-adjudication-reason">Reason: {reason}</span> : null}
      <p className="pb-adjudication-note">
        {noteText}{' '}
        Connect your provider key in <a href="/settings/provider-keys">Provider Keys</a>.
      </p>
    </div>
  );
}

export function CandidateRow({
  candidate,
  projectId,
  onReviewed,
}: {
  candidate: MemoryCandidateView;
  projectId: string;
  onReviewed: () => void;
}): React.ReactElement {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewMessage, setReviewMessage] = useState<string | null>(null);

  const handleDecision = async (decision: 'approve' | 'reject') => {
    setSubmitting(true);
    setReviewMessage(null);
    try {
      const result = await reviewMemoryCandidate(
        candidate.candidate_id,
        decision,
        note,
        projectId,
      );
      setReviewMessage(`Review recorded as ${result.state}.`);
      onReviewed();
    } catch (error: unknown) {
      if (error instanceof BrainApiError) {
        setReviewMessage(`Review failed: server returned status ${error.status}.`);
      } else {
        setReviewMessage('Review request failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className="pb-authority-row pb-candidate-row" key={candidate.candidate_id}>
      <div className="pb-candidate-meta">
        <span className="pb-candidate-origin">
          origin: {candidate.origin || 'unknown'}
        </span>
        <span className="pb-candidate-source-ref">
          source: <code>{candidate.source_ref || '(none)'}</code>
        </span>
        <time className="pb-candidate-created" dateTime={candidate.created_at}>
          {candidate.created_at}
        </time>
      </div>

      <div className="pb-candidate-claim">
        <p className="pb-candidate-claim-text">{candidate.claim || candidate.text}</p>
      </div>

      <AdjudicationDisplay adjudication={candidate.adjudication} />

      <div className="pb-candidate-actions">
        <label className="pb-candidate-note-field">
          Review note (optional)
          <input
            type="text"
            className="pb-candidate-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add note for this decision"
            disabled={submitting}
          />
        </label>
        <div className="pb-candidate-buttons">
          <button
            type="button"
            className="pb-candidate-btn-approve"
            onClick={() => handleDecision('approve')}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Approve'}
          </button>
          <button
            type="button"
            className="pb-candidate-btn-reject"
            onClick={() => handleDecision('reject')}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Reject'}
          </button>
        </div>
      </div>

      {reviewMessage && <p className="pb-candidate-status-message">{reviewMessage}</p>}
    </li>
  );
}

export function PendingReviewPanel({ projectId }: PendingReviewPanelProps): React.ReactElement {
  const [candidates, setCandidates] = useState<MemoryCandidateView[]>([]);
  const [state, setState] = useState<LoadState>('loading');

  const loadCandidates = useCallback(() => {
    setState('loading');
    fetchPendingMemoryCandidates(projectId)
      .then((items) => {
        setCandidates(items);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && (error.status === 404 || error.status === 503)) {
          setState('unavailable');
        } else {
          setState('error');
        }
      });
  }, [projectId]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  if (state === 'loading') {
    return <p className="pb-section-state">Loading pending candidates…</p>;
  }

  if (state === 'unavailable') {
    return (
      <div className="pb-section-state pb-pending-unavailable">
        Unavailable — not found, or the Brain service is unavailable at this deployment.
      </div>
    );
  }

  if (state === 'error') {
    return (
      <p className="pb-section-state pb-pending-error">
        Could not load pending candidates.{' '}
        <button type="button" onClick={loadCandidates}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="pb-authority-block pb-pending-review-panel" aria-label="Pending memory candidates">
      <h4>Pending memory candidate reviews</h4>
      <p className="pb-plane-note">
        Memory candidates awaiting human adjudication. The UI never decides lifecycle state:
        only a human can approve or reject, and candidates never become trusted memory until approved.
      </p>

      {candidates.length === 0 ? (
        <p className="pb-empty pb-pending-empty">
          No pending-review memory candidates for this project. New candidates requiring review will appear here.
        </p>
      ) : (
        <ul className="pb-authority-list pb-pending-candidate-list">
          {candidates.map((candidate) => (
            <CandidateRow
              key={candidate.candidate_id}
              candidate={candidate}
              projectId={projectId}
              onReviewed={loadCandidates}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export default PendingReviewPanel;
